// Fee valuing and scoping, shared by FeeTransparency, CalculatorView,
// UnitsView and OptionsBox/UnitView so every surface agrees on the same
// numbers and the same fee set.

const RANGE_SEPARATOR = " – ";

export const FEE_LEVEL = {
  PROPERTY: "property",
  FLOOR_PLAN: "floorplan",
  UNIT: "unit",
};

// FLOOR_PLAN_CARD — browsing: every unit fee under the floor plan, tagged.
// UNIT           — quoting: only the selected unit's fees.
// ALL            — no floor plan / unit context available (AMA answers).
export const FEE_SCOPE_MODE = {
  FLOOR_PLAN_CARD: "floorPlanCard",
  UNIT: "unit",
  ALL: "all",
};

// ── Parsing ────────────────────────────────────────────────────────────────

/** Amounts arrive as numbers (AMA) or strings (find_apartments). */
export const parseFeeAmount = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();

    if (trimmed === "") {
      return null;
    }

    const parsed = parseFloat(trimmed.replace(/[$,\s]/g, ""));

    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const money = (n) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// 200.00 -> "200", 2.50 -> "2.5"
const trimTrailingZeros = (n) => String(parseFloat(n.toFixed(2)));

const trimmed = (value) => (typeof value === "string" ? value.trim() : "");

// Engrain does not guarantee min <= max, so never trust the order. Both bounds
// are required — a half-parsed range has no displayable value.
const parseRange = (fee) => {
  const a = parseFeeAmount(fee.min_amount);
  const b = parseFeeAmount(fee.max_amount);

  if (a === null || b === null) {
    return [null, null];
  }

  return [Math.min(a, b), Math.max(a, b)];
};

// ── Display ────────────────────────────────────────────────────────────────

/**
 * amount -> "$50.00" · range -> "$400.00 – $600.00" · text -> "Varies"
 * percentage -> "200% of Base Rent"
 *
 * `count` multiplies amounts and ranges only. `fallback` covers the no-value
 * case, which differs by caller: "Usage-Based" in the summary, "" elsewhere.
 */
export const formatFeeValue = (fee, { count = 1, fallback = "" } = {}) => {
  if (!fee) {
    return fallback;
  }

  if (fee.value_type === "text") {
    return trimmed(fee.text_amount) || fallback;
  }

  if (fee.value_type === "percentage") {
    const percentage = parseFeeAmount(fee.percentage_amount);

    if (percentage === null) {
      return fallback;
    }

    // percentage_ref is a legacy misspelling still present in older callers.
    // With no reference we print the bare percentage rather than guessing what
    // it applies to — "200% of base rent" and "200% of the remaining lease" are
    // wildly different sums, and stating the wrong one misleads on real money.
    const reference = trimmed(fee.percentage_reference) || trimmed(fee.percentage_ref);
    const value = `${trimTrailingZeros(percentage)}%`;

    return reference ? `${value} of ${reference}` : value;
  }

  if (fee.value_type === "range") {
    const [lo, hi] = parseRange(fee);

    if (lo === null) {
      return fallback;
    }

    return lo === hi ? money(lo * count) : `${money(lo * count)}${RANGE_SEPARATOR}${money(hi * count)}`;
  }

  const amount = parseFeeAmount(fee.amount ?? fee.min_amount);

  return amount === null ? fallback : money(amount * count);
};

// ── Totals ─────────────────────────────────────────────────────────────────

/**
 * What a fee contributes to a total. Ranges contribute their low end; text and
 * percentage contribute nothing — they are displayed but not summed.
 */
export const getFeeNumericAmount = (fee) => {
  if (!fee) {
    return 0;
  }

  if (fee.value_type === "range") {
    const [lo] = parseRange(fee);

    return lo ?? 0;
  }

  if (fee.value_type === "text" || fee.value_type === "percentage") {
    return 0;
  }

  return parseFeeAmount(fee.amount ?? fee.min_amount) ?? 0;
};

/** Numeric price when there is one, else the sentinel sortByPriceThenName sinks. */
export const getFeeSortValue = (fee) => {
  if (!fee || fee.value_type === "text" || fee.value_type === "percentage") {
    return "Usage-Based";
  }

  const low = fee.value_type === "range" ? parseRange(fee)[0] : parseFeeAmount(fee.amount ?? fee.min_amount);

  return low === null ? "Usage-Based" : low;
};

/** True when a fee's real cost cannot be pinned down, so totals need a "+". */
export const isEstimatedFeeValue = (fee) => fee?.value_type === "text" || fee?.value_type === "range" || fee?.value_type === "percentage";

/** due_at_timing (legacy) and due_timing (current) both appear; "" means unset. */
export const getFeeDueTiming = (fee) => {
  const raw = fee?.due_at_timing !== undefined ? fee.due_at_timing : fee?.due_timing;

  return trimmed(raw) || null;
};

// ── Scoping ────────────────────────────────────────────────────────────────

const scopeId = (scope) => {
  if (!scope || typeof scope !== "object") {
    return null;
  }

  return trimmed(String(scope.id ?? "")) || null;
};

const sameId = (a, b) => {
  if (a == null || b == null) {
    return false;
  }

  const left = String(a).trim();

  return left !== "" && left === String(b).trim();
};

/**
 * A fee is scoped to a unit OR a floor plan, never both — the API attaches the
 * unit and leaves floor_plan as {}. Empty object, empty string and null all
 * mean "not scoped" -> property level.
 */
export const getFeeLevel = (fee) => {
  if (scopeId(fee?.unit)) {
    return FEE_LEVEL.UNIT;
  }

  if (scopeId(fee?.floor_plan)) {
    return FEE_LEVEL.FLOOR_PLAN;
  }

  return FEE_LEVEL.PROPERTY;
};

/**
 * Narrow the property-wide fee array to what applies to a selection.
 *
 * Property fees always survive. Floor plan fees survive on a matching floor
 * plan. Unit fees depend on the mode: every unit of this floor plan for
 * FLOOR_PLAN_CARD, only the selected one for UNIT, all of them for ALL.
 * Disabled fees are dropped in every mode so all consumers share one set.
 *
 * A unit fee carries no floor plan reference, so membership is decided by
 * looking the unit up in the floor plan's own units[] — never via
 * unit.floor_plan_type, which is a display label rather than an id.
 */
export const filterFeesForContext = (fees, { floorPlan = null, unit = null, mode = FEE_SCOPE_MODE.FLOOR_PLAN_CARD } = {}) => {
  if (!Array.isArray(fees)) {
    return [];
  }

  const active = fees.filter((fee) => fee?.is_enabled !== false);

  if (mode === FEE_SCOPE_MODE.ALL) {
    return active;
  }

  const floorPlanId = floorPlan?.id ?? unit?.fp_id ?? null;

  const allowedUnitIds = mode === FEE_SCOPE_MODE.FLOOR_PLAN_CARD ? (Array.isArray(floorPlan?.units) ? floorPlan.units : []).map((u) => u?.id) : [unit?.id];

  return active.filter((fee) => {
    const level = getFeeLevel(fee);

    if (level === FEE_LEVEL.PROPERTY) {
      return true;
    }

    if (level === FEE_LEVEL.FLOOR_PLAN) {
      return sameId(fee.floor_plan.id, floorPlanId);
    }

    return allowedUnitIds.some((id) => sameId(fee.unit.id, id));
  });
};

// ── Applicability tag ──────────────────────────────────────────────────────

const collectUnits = (floorPlans, units) => [
  ...(Array.isArray(units) ? units : []),
  ...(Array.isArray(floorPlans) ? floorPlans : []).flatMap((fp) => (Array.isArray(fp?.units) ? fp.units : [])),
];

/**
 * What a scoped fee should be labelled with, as `{ level, name }` — the caller
 * renders the copy so it can run through i18n. Null for property-level fees.
 *
 * The name comes from the API first, then a local lookup by id. When neither
 * yields one, `name` is "" and the caller substitutes a placeholder: the tag
 * still has to appear, or a unit fee would be indistinguishable from a
 * property fee and read as applying to everyone.
 */
export const resolveFeeScope = (fee, { floorPlans = [], units = [] } = {}) => {
  const level = getFeeLevel(fee);

  if (level === FEE_LEVEL.PROPERTY) {
    return null;
  }

  if (level === FEE_LEVEL.UNIT) {
    const match = collectUnits(floorPlans, units).find((u) => sameId(u?.id, fee.unit.id));

    return { level, name: trimmed(fee.unit.name) || trimmed(match?.unit_name) };
  }

  const match = (Array.isArray(floorPlans) ? floorPlans : []).find((fp) => sameId(fp?.id, fee.floor_plan.id));

  return { level, name: trimmed(fee.floor_plan.name) || trimmed(match?.fp_name) };
};

/**
 * Stamp resolved scope names onto a fee list, for the AMA path where the array
 * travels to the UI with no floor plan list beside it. Fees the API already
 * named are untouched; non-array input passes through so callers keep null.
 */
export const withResolvedScopeNames = (fees, context = {}) => {
  if (!Array.isArray(fees)) {
    return fees;
  }

  return fees.map((fee) => {
    const resolved = resolveFeeScope(fee, context);

    if (!resolved?.name) {
      return fee;
    }

    const key = resolved.level === FEE_LEVEL.UNIT ? "unit" : "floor_plan";

    if (trimmed(fee[key]?.name)) {
      return fee;
    }

    return { ...fee, [key]: { ...fee[key], name: resolved.name } };
  });
};
